import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePrices } from "@/contexts/prices-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { toHumanReadableAmount } from "@/lib/string";
import { cn } from "@/lib/utils";
import { TeamBillingPriceDTO, TeamLicenseInventoryDTO } from "@shaarp/common";
import { useEffect } from "react";

export default function SelectPrice({
  value: priceId,
  defaultValue = undefined,
  required = false,
  disabled = false,
  inventory = undefined,
  onChange = () => {},
}: {
  value: string;
  defaultValue?: string;
  required?: boolean;
  disabled?: boolean;
  /** When provided, prices with no remaining license are disabled. */
  inventory?: TeamLicenseInventoryDTO;
  onChange?: (newPriceId: string) => void;
}) {
  const { prices, apiFetchPrices } = usePrices();
  const { t } = useTranslation();

  useEffect(() => {
    const init = async () => {
      await apiFetchPrices();
    };
    init();
  }, []);

  // Derived during render so the selection always reflects the current `value`
  // prop (including a pre-selected default) as soon as prices are loaded, with
  // no effect-timing races.
  const pricesMonth = prices.filter((price) => price.interval === "month");
  const pricesYear = prices.filter((price) => price.interval === "year");
  const priceSelected = prices.find((price) => price.id === priceId);

  // Free (admin-managed) plans are monthly-only with no billing: hide the
  // frequency grouping, prices and the monthly/annual labels entirely.
  const isFreePlan = inventory?.freePlan ?? false;

  const handleOnChange = (newPriceId: string) => {
    onChange(newPriceId);
  };

  /**
   * Remaining licenses for a price, or null when availability is unknown
   * (no inventory provided or the price has no mapped licence type).
   */
  const getAvailable = (price: TeamBillingPriceDTO): number | null => {
    if (!inventory) return null;
    const freq = price.interval === "year" ? inventory.annual : inventory.monthly;
    if (price.licenceType === "professional") return freq.pro.available;
    return null;
  };

  const isOutOfStock = (price: TeamBillingPriceDTO): boolean => {
    const available = getAvailable(price);
    return available !== null && available <= 0;
  };

  // An empty picker reads as "no licence to pick", so land on the first
  // in-stock option (in display order) as soon as the prices resolve.
  useEffect(() => {
    if (priceId || disabled || prices.length === 0) return;
    const firstAvailable = (
      isFreePlan ? pricesMonth : [...pricesMonth, ...pricesYear]
    ).find((price) => !isOutOfStock(price));
    if (firstAvailable) onChange(firstAvailable.id);
  }, [prices, priceId, disabled, isFreePlan, inventory]);

  /**
   * Badge showing the remaining-license count next to an available option, so
   * the picker mirrors the stock hint shown when changing an existing user's
   * license.
   */
  const renderStockBadge = (price: TeamBillingPriceDTO) => {
    const available = getAvailable(price);
    if (available === null || available <= 0) return null;
    return (
      <Badge
        variant="default"
        className="rounded-full text-[10px] px-2 py-0 ml-1"
      >
        {available}
      </Badge>
    );
  };

  return (
    <Select
      defaultValue={defaultValue}
      value={priceSelected?.id || ""}
      onValueChange={handleOnChange}
      required={required}
      disabled={disabled}
    >
      <SelectTrigger>
        <SelectValue>
          {priceSelected
            ? isFreePlan
              ? priceSelected.productName
              : `${priceSelected.productName} (${priceSelected.interval === "year" ? t("components.selectPrice.annual") : t("components.selectPrice.monthly")})`
            : ""}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {isFreePlan && (
          <SelectGroup>
            {pricesMonth.map((price) => (
              <SelectItem
                key={price.id}
                value={price.id}
                disabled={isOutOfStock(price)}
                className={cn(
                  price.licenceType === null && "bg-warning-foreground",
                )}
              >
                <span className="flex items-center gap-2">
                  {price.productName}
                  {isOutOfStock(price)
                    ? ` (${t("components.selectPrice.noneRemaining")})`
                    : renderStockBadge(price)}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {!isFreePlan && (
          <>
        {pricesMonth.length > 0 && (
          <SelectGroup>
            <SelectLabel>
              {t("components.selectPrice.monthlyLabel")}
            </SelectLabel>
            {pricesMonth.map((price) => (
              <SelectItem
                key={price.id}
                value={price.id}
                disabled={isOutOfStock(price)}
                className={cn(
                  price.licenceType === null && "bg-warning-foreground",
                )}
              >
                <span className="flex items-center gap-2">
                  {price.productName} - {toHumanReadableAmount(price.amount)}{" "}
                  {t("components.selectPrice.perMonth")}
                  {isOutOfStock(price)
                    ? ` (${t("components.selectPrice.noneRemaining")})`
                    : renderStockBadge(price)}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {pricesYear.length > 0 && (
          <SelectGroup>
            <SelectLabel>{t("components.selectPrice.annualLabel")}</SelectLabel>
            {pricesYear.map((price) => (
              <SelectItem
                key={price.id}
                value={price.id}
                disabled={isOutOfStock(price)}
                className={cn(
                  price.licenceType === null && "bg-warning-foreground",
                )}
              >
                <span className="flex items-center gap-2">
                  {price.productName} - {toHumanReadableAmount(price.amount)}{" "}
                  {t("components.selectPrice.perYear")}
                  {isOutOfStock(price)
                    ? ` (${t("components.selectPrice.noneRemaining")})`
                    : renderStockBadge(price)}
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
          </>
        )}
      </SelectContent>
    </Select>
  );
}
